import { type MutableRefObject, useEffect, useRef, useState, useCallback } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuth } from '@/contexts/AuthContext';
import { getAuth } from 'firebase/auth';

const API_WS_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL?.replace(/\/v1$/, '') ?? 'http://localhost:3001';
const IMPERSONATION_KEY = 'enska_support_token';

async function resolveToken(): Promise<string | null> {
  const imp = sessionStorage.getItem(IMPERSONATION_KEY);
  if (imp) return imp;
  try {
    const user = getAuth().currentUser;
    if (user) return await user.getIdToken();
  } catch { /* not signed in */ }
  return null;
}

export interface AdminMessageEvent {
  conversationId: string;
  handoffId?: string;
  message: {
    id: string;
    content: string;
    senderType: string;
    createdAt: string;
  };
}

export interface HandoffEvent {
  handoffId: string;
  conversationId?: string;
  status?: string;
  priority?: string;
  teamId?: string;
  /** Full updated handoff object (present on assign/resolve) */
  handoff?: Record<string, unknown>;
}

type Handler<T> = (data: T) => void;

/**
 * useAdminSocket — connects to /admin namespace.
 * Delivers real-time message and handoff events to operator UI.
 * Operators connect with their tenantId so they join the right room.
 */
export function useAdminSocket(opts?: {
  onMessage?: Handler<AdminMessageEvent>;
  onHandoffCreated?: Handler<HandoffEvent>;
  onHandoffResolved?: Handler<HandoffEvent>;
  onHandoffAssigned?: Handler<HandoffEvent>;
}): { connected: boolean; emit: (event: string, data: unknown) => void; socket: MutableRefObject<Socket | null> } {
  const { platformUser } = useAuth();
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (!platformUser?.tenantId) return;
    let active = true;

    async function connect() {
      const token = await resolveToken();
      if (!token || !active) return;

      const socket = io(`${API_WS_URL}/admin`, {
        auth: { token },
        query: { tenantId: platformUser!.tenantId },
        transports: ['websocket'],
        reconnectionDelay: 3000,
        reconnectionAttempts: 10,
      });

      socketRef.current = socket;

      socket.on('connect', () => { if (active) setConnected(true); });
      socket.on('disconnect', () => setConnected(false));

      socket.on('message:new', (data: AdminMessageEvent) => {
        optsRef.current?.onMessage?.(data);
      });

      socket.on('handoff:created', (data: HandoffEvent) => {
        optsRef.current?.onHandoffCreated?.(data);
      });

      socket.on('handoff:resolved', (data: HandoffEvent) => {
        optsRef.current?.onHandoffResolved?.(data);
      });

      socket.on('handoff:assigned', (data: HandoffEvent) => {
        optsRef.current?.onHandoffAssigned?.(data);
      });

      socket.on('handoff:updated', (data: HandoffEvent) => {
        optsRef.current?.onHandoffAssigned?.(data);
      });
    }

    void connect();

    return () => {
      active = false;
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [platformUser?.tenantId]);

  /** Emit socket events (e.g. resolve handoff) */
  const emit = useCallback((event: string, data: unknown) => {
    socketRef.current?.emit(event, data);
  }, []);

  return { connected, emit, socket: socketRef };
}
